import React, { useEffect } from "react";

/**
 * Reusable Modal component with modern styling
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls if the modal is visible
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Content to display in the modal body
 * @param {Array} props.buttons - Array of button objects with text, onClick, color, disabled
 * @param {boolean} props.showSpinner - Whether to show loading spinner
 * @param {string} props.spinnerText - Main text to show with spinner
 * @param {string} props.spinnerSubText - Additional text to show with spinner
 * @param {string} props.size - Size of modal: 'sm', 'md', 'lg' (default: 'md')
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  buttons = [],
  showSpinner = false,
  spinnerText = "Processing",
  spinnerSubText = "Please wait...",
  size = "md",
}) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Calculate modal width based on size
  const sizeClasses = {
    sm: "modal-container-sm",
    md: "modal-container-md",
    lg: "modal-container-lg",
  };

  const modalSizeClass = sizeClasses[size] || sizeClasses.md;

  // Determine if this is a spinner-only modal
  const isSpinnerModal =
    showSpinner && !title && !children && buttons.length === 0;

  // Handle click on backdrop to close modal if onClose is provided
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className={`modal-container ${modalSizeClass}`}>
        {isSpinnerModal ? (
          // Spinner-only mode
          <div className="modal-spinner-content">
            <div className="modal-spinner"></div>
            <h3 className="modal-spinner-title">{spinnerText}</h3>
            {spinnerSubText && (
              <p className="modal-spinner-subtitle">{spinnerSubText}</p>
            )}
          </div>
        ) : (
          // Regular modal mode
          <>
            {/* Close button */}
            {onClose && (
              <button className="modal-close-button" onClick={onClose}>
                ×
              </button>
            )}

            {/* Show spinner at the top of content if requested */}
            {showSpinner && (
              <div className="modal-loading">
                <div className="modal-spinner modal-spinner-small"></div>
              </div>
            )}

            {/* Title */}
            {title && <h3 className="modal-title">{title}</h3>}

            {/* Content */}
            {children && <div className="modal-body">{children}</div>}

            {/* Buttons */}
            {buttons.length > 0 && (
              <div
                className={`modal-footer ${
                  buttons.length >= 3 ? "modal-footer-stacked" : ""
                }`}
              >
                {buttons.map((button, index) => {
                  // Determine button style based on color prop
                  const buttonClasses = [
                    "modal-button",
                    `modal-button-${button.color || "default"}`,
                  ].join(" ");

                  return (
                    <button
                      key={index}
                      className={buttonClasses}
                      onClick={button.onClick}
                      disabled={button.disabled || false}
                    >
                      {button.text}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Modal;
